import React from 'react';
import { ListItem, ListItemIcon, ListItemText } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useTheme } from '@mui/material/styles';
import {
  getActionListItemStyles,
  getListItemIconStyles,
  listItemTextStyles,
  listItemTextTypographyProps
} from './navigationStyles';

/**
 * ImpersonationControls component for rendering impersonation-related controls
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isImpersonating - Whether user impersonation is active
 * @param {boolean} props.hasAdminAccess - Whether the user has admin access
 * @param {boolean} props.miniDrawer - Whether the drawer is in mini mode
 * @param {boolean} props.isDesktop - Whether the current view is desktop
 * @param {Function} props.onStartImpersonation - Function to start impersonation
 * @param {Function} props.onStopImpersonation - Function to stop impersonation
 * @returns {JSX.Element|null} ImpersonationControls component or null if not applicable
 */
const ImpersonationControls = ({ 
  isImpersonating, 
  hasAdminAccess, 
  miniDrawer, 
  isDesktop,
  onStartImpersonation, 
  onStopImpersonation 
}) => {
  const theme = useTheme();
  
  // Don't render if user doesn't have admin access and isn't currently impersonating
  if (!hasAdminAccess && !isImpersonating) return null;
  
  return (
    <>      
      {/* Start Impersonation - Only for admins who aren't currently impersonating */}
      {hasAdminAccess && !isImpersonating && (
        <ListItem
          onClick={onStartImpersonation}
          sx={getActionListItemStyles(theme)}
        >
          <ListItemIcon sx={getListItemIconStyles()}>
            <PersonAddIcon />
          </ListItemIcon>
          {(!isDesktop || !miniDrawer) && <ListItemText sx={listItemTextStyles} primaryTypographyProps={listItemTextTypographyProps} primary="Impersonate User" />}
        </ListItem>
      )}
      
      {/* Stop Impersonation - Only when currently impersonating */}
      {isImpersonating && (
        <ListItem
          onClick={onStopImpersonation}
          sx={{
            ...getActionListItemStyles(theme),
            color: theme.palette.warning.main,
            border: `var(--border-width) solid ${theme.palette.warning.main}`,

            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <ListItemIcon sx={getListItemIconStyles()}>
            <SwapHorizIcon />
          </ListItemIcon>
          {(!isDesktop || !miniDrawer) && <ListItemText sx={listItemTextStyles} primaryTypographyProps={listItemTextTypographyProps} primary="Stop Impersonating" />}
        </ListItem>
      )}
    </>
  );
};

export default ImpersonationControls;
